// frontend/src/pages/StoreActivity.jsx
// Staff-only: one store, read the way a non-technical reader asks about it.
//
// Master-detail: a searchable store rail on the left (stacked above on a
// narrow screen, where it folds away once a store is picked); beside it the
// sections in the order the questions come — who is this store (header), then,
// collapsed right under it, the record-level details; how big is it and can it
// use the app (glance strip), is anyone using it (engagement), what do they
// use and what is the system doing for them (feature areas over connection,
// scheduled work, anomalies — left column) beside where they go and what
// Shopify and connected systems sent us (pages, webhooks — right column), what
// we mailed them or our team about them (emails), and what happened
// (timeline).
//
// Data: /api/analytics/insights/stores/ (picker) and
//       /api/analytics/insights/store-activity/?store_id= (drill-down).
// Route: /store-activity, or /store-activity/store/:store opened on one store.

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import PageBanner from '../components/common/PageBanner';
import { ICON, Storefront } from '../icons';
import { sessionApi } from '../services/SessionApiService';
import { apiErrorFrom } from '../utils/parseApiError';
import { translateRequestError } from '../queries/api';
import { useTabParams } from '../tabs/useTabParams';
import { decodeRouteSegment } from '../tabs/routeSegment';
import StorePickerRail from '../components/staff/StorePickerRail';
import StoreHeader from './storeActivity/StoreHeader';
import GlanceStrip from './storeActivity/GlanceStrip';
import EngagementPanel from './storeActivity/EngagementPanel';
import FeatureAreasPanel from './storeActivity/FeatureAreasPanel';
import PagesPanel from './storeActivity/PagesPanel';
import WebhooksPanel from './storeActivity/WebhooksPanel';
import TimelinePanel from './storeActivity/TimelinePanel';
import EmailsPanel from './storeActivity/EmailsPanel';
import { AnomaliesPanel, AutomationPanel, ConnectionPanel } from './storeActivity/SystemPanel';
import AccountDetails from './storeActivity/AccountDetails';
import '../styles/pages.css';
import '../styles/store-activity.css';

function StoreActivity() {
  const { t } = useTranslation();
  // `/store-activity/store/:store` opens the page on one store; the rail
  // still switches freely from there.
  const { store: storeParam } = useTabParams();

  const [selectedId, setSelectedId] = useState(() => decodeRouteSegment(storeParam) || null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // selectedId only ever goes null → id (the picker has no deselect), so
  // no clearing branch is needed — data stays null until the first pick.
  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await sessionApi.get(
          `/api/analytics/insights/store-activity/?store_id=${encodeURIComponent(selectedId)}`,
        );
        if (!res.ok) throw await apiErrorFrom(res, 'GET /api/analytics/insights/store-activity/');
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        console.error('storeActivity load:', e);
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedId]);

  return (
    <div className="page-container">
      <div className="sa-shell">
        <StorePickerRail selectedId={selectedId} onSelect={setSelectedId} />

        <main className="sa-detail">
          <PageBanner severity="error" messages={[error && translateRequestError(error, t, 'storeActivity.loadFailed')]} />
          {loading && <div className="sa-empty"><CircularProgress size={28} /></div>}

          {!selectedId && !loading && !error && (
            <div className="sa-empty">
              <Storefront size={ICON.display} weight="thin" />
              <span className="sa-empty-title">{t('storeActivity.title')}</span>
              <span>{t('storeActivity.picker.selectPrompt')}</span>
            </div>
          )}

          {data && !loading && <Dashboard data={data} />}
        </main>
      </div>
    </div>
  );
}

function Dashboard({ data }) {
  const windowDays = data.page_view_window_days;
  return (
    <>
      <StoreHeader
        store={data.store}
        profile={data.profile}
        billing={data.billing}
        people={data.people}
        lastSeen={data.engagement.last_seen}
      />
      <AccountDetails
        profile={data.profile}
        billing={data.billing}
        people={data.people}
        searches={data.searches}
        searchesTruncated={data.truncated.searches}
      />
      <GlanceStrip footprint={data.footprint} billing={data.billing} />

      <EngagementPanel engagement={data.engagement} />

      {/* Two explicit columns rather than a packed grid: auto-placement left
          holes beside the tallest panel and dropped the next one into a
          half-width slot below. */}
      <div className="sa-columns">
        <div className="sa-column">
          <FeatureAreasPanel areas={data.feature_areas} windowDays={windowDays} />
          <ConnectionPanel profile={data.profile} />
          <AutomationPanel automation={data.automation} windowDays={windowDays} />
          <AnomaliesPanel anomalies={data.anomalies} truncated={data.truncated.anomalies} />
        </div>
        <div className="sa-column">
          <PagesPanel pages={data.pages} windowDays={windowDays} />
          <WebhooksPanel webhooks={data.webhooks} windowDays={windowDays} />
        </div>
      </div>

      <EmailsPanel emails={data.emails} truncated={data.truncated.emails} />

      <TimelinePanel store={data.store} timeline={data.timeline} truncated={data.truncated.timeline} />
    </>
  );
}

export default StoreActivity;
